import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  alpha,
  Box,
  Button,
  Radio,
  Skeleton,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import React, { useCallback, useMemo } from 'react';

import {
  CURRENTLY_UNAVAILABLE_LABEL,
  NOT_SELECTED_TEXT,
  OPTIONS_STATUS_SKELETON_WIDTH,
  SOLD_OUT_REMOVED_LABEL,
  UNAVAILABLE_CARD_OPACITY,
} from '../../../constants/marketplace';
import useWindowSize from '../../../hooks/useWindowSize';
import { IPickedMskuItem } from '../../../types/api';
import { ICampaignItem } from '../../../types/pyg';
import { HorizontalCardVariantEnum } from '../../../types/shell';
import { getPluralForm } from '../../../util/helpers';
import { HorizontalCard } from './HorizontalCard';
import { VendorLine } from './VendorLine';

interface IProps {
  item: ICampaignItem;
  vendor?: string;
  selected: IPickedMskuItem | null;
  onExpand: React.Dispatch<React.SetStateAction<string>>;
  onSelect: (item: IPickedMskuItem) => void;
  isExpandable?: boolean;
  expanded?: boolean;
  onViewDetails?: (skuOption: ICampaignItem, onSelectOption?: () => void) => void;
  disabledItems?: string[];
  isLoading?: boolean;
  hasFailed?: boolean;
  isOutOfStock?: boolean;
  onRetry?: () => void;
}

const optionDetailsButtonSx: SxProps<Theme> = { minWidth: 64, height: 30, flexShrink: 0 };

const accordionSx: SxProps<Theme> = {
  '&.MuiAccordion-root': {
    margin: 0,
    border: 1,
    borderColor: 'divider',
  },
  '&:before': {
    display: 'none',
  },
};

const accordionSummarySx: SxProps<Theme> = {
  px: 0,
  minHeight: 'unset',
  borderBottom: 1,
  borderColor: 'divider',
  boxShadow: 'none',
  '&.Mui-expanded': {
    minHeight: 'unset',
  },
  '& .MuiAccordionSummary-content': {
    my: 0,
    '&.Mui-expanded': {
      my: 0,
    },
  },
};

const accordionDetailsSx: SxProps<Theme> = {
  p: 2,
  pt: 1,
  '&.MuiAccordionDetails-root': {
    backgroundColor: ({ palette }) => alpha(palette.primary.main, palette.action.hoverOpacity),
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
};

const bodySx: SxProps<Theme> = { display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%' };

const statusRowSx: SxProps<Theme> = { display: 'flex', gap: 1, alignItems: 'center', minWidth: 0, width: '100%' };

const inlineButtonSx: SxProps<Theme> = { p: 0, minWidth: 0, '&:hover': { backgroundColor: 'transparent' } };

const soldOutStatusSx: SxProps<Theme> = { display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 };

const soldOutIconSx: SxProps<Theme> = { fontSize: 20, color: 'warning.main', flexShrink: 0 };

const optionsHeaderSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
};

export const ItemPicker: React.FC<IProps> = ({
  item,
  vendor,
  expanded,
  onExpand,
  onSelect,
  isExpandable,
  selected,
  onViewDetails,
  disabledItems,
  isLoading = false,
  hasFailed = false,
  isOutOfStock = false,
  onRetry,
}) => {
  const { name, image_url, sku_options, id, quantity = 1, internalId = '' } = item;
  const { mobile } = useWindowSize();

  const skuOptionsCount = sku_options?.length ?? 0;
  const hasSkuOptions = skuOptionsCount > 0;

  const handleToggleItem = useCallback(() => {
    if (typeof onExpand === 'function' && isExpandable) {
      onExpand(expanded ? '' : internalId);
    }
  }, [internalId, onExpand, expanded, isExpandable]);

  const handleSelectItem = useCallback(
    (uid: string) => {
      const count: number | undefined = sku_options?.find(({ id: optionId }) => optionId === uid)?.count;
      onSelect({ uid, parent_id: id, quantity, count });
    },
    [id, quantity, sku_options, onSelect],
  );

  const handleViewDetails = useCallback(
    (
      e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
      viewedItem: ICampaignItem | undefined,
      onSelectOption?: () => void,
    ) => {
      if (!viewedItem) {
        return;
      }
      e.stopPropagation();
      onViewDetails?.(viewedItem, onSelectOption);
    },
    [onViewDetails],
  );

  const handleRetryClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onRetry?.();
    },
    [onRetry],
  );

  const pickedOption = useMemo(
    () => sku_options?.find((option) => option.id === selected?.uid),
    [sku_options, selected],
  );

  const statusText = isOutOfStock ? SOLD_OUT_REMOVED_LABEL : NOT_SELECTED_TEXT;

  const pickedTextSx: SxProps<Theme> = useMemo(
    () => ({
      color: 'success.main',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      ...(mobile && { maxWidth: 240 }),
    }),
    [mobile],
  );

  const chevronSx: SxProps<Theme> = useMemo(
    () => ({
      color: 'action.active',
      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: ({ transitions }) => transitions.create('transform', { duration: transitions.duration.standard }),
    }),
    [expanded],
  );

  const renderItem = useMemo(() => {
    const isPicked = !!pickedOption;
    const isPickedShown = !hasFailed && isPicked;
    const isOptionsLoadingShown = !hasFailed && !isPicked && isLoading;
    const isStatusShown = !hasFailed && !isPicked && !isLoading && (hasSkuOptions || isOutOfStock);
    const isRetryShown = hasFailed && !!onRetry;
    const isChevronShown = !isLoading && hasSkuOptions && !isOutOfStock;

    return (
      <HorizontalCard
        image_url={isPicked ? pickedOption?.image_url : image_url}
        name={name}
        showCheckmarkBadge={isPicked}
        hasThumbnailBorder
        isSoldOut={isOutOfStock}
        body={
          <Box sx={bodySx}>
            {vendor && (
              <Box sx={{ mb: 0.5 }}>
                <VendorLine vendor={vendor} />
              </Box>
            )}
            <Box sx={statusRowSx}>
              {hasFailed && (
                <Typography variant="body2" color="error.main">
                  Could not load options
                </Typography>
              )}
              {isRetryShown && (
                <Button variant="text" size="small" color="primary" onClick={handleRetryClick} sx={inlineButtonSx}>
                  Retry
                </Button>
              )}
              {isPickedShown && (
                <Typography component="span" variant="body2" sx={pickedTextSx}>
                  Selected: {pickedOption?.name}
                </Typography>
              )}
              {isOptionsLoadingShown && (
                <Typography component="span" variant="body2">
                  <Skeleton variant="text" width={OPTIONS_STATUS_SKELETON_WIDTH} />
                </Typography>
              )}
              {isStatusShown && (
                <Box sx={soldOutStatusSx}>
                  {isOutOfStock && <RemoveCircleOutlineIcon sx={soldOutIconSx} />}
                  <Typography variant="body2" color={isOutOfStock ? 'warning.main' : 'text.secondary'}>
                    {statusText}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        }
        sx={{ border: 'none' }}
      >
        {isChevronShown && <KeyboardArrowDownIcon sx={chevronSx} />}
      </HorizontalCard>
    );
  }, [
    image_url,
    name,
    vendor,
    pickedOption,
    isLoading,
    hasFailed,
    hasSkuOptions,
    onRetry,
    handleRetryClick,
    pickedTextSx,
    chevronSx,
    statusText,
    isOutOfStock,
  ]);

  const renderSkuOptions = useMemo(() => {
    return sku_options?.map((skuOption: ICampaignItem) => {
      const isUnavailable = skuOption.is_out_of_stock === true;
      const isOptionOutOfStock =
        isUnavailable || ((skuOption.count === 0 || skuOption.count === -1) && !skuOption.preorder_allowed);
      const isItemPicked = selected?.uid === skuOption.id;
      const isDisabled = (!isItemPicked && disabledItems?.includes(skuOption.id)) || isOptionOutOfStock;

      return (
        <Box onClick={() => !isDisabled && handleSelectItem(skuOption.id)} key={skuOption.id}>
          <HorizontalCard
            image_url={skuOption.image_url}
            name={skuOption.name}
            body={
              isUnavailable ? (
                <Typography variant="body2" color="text.secondary">
                  {CURRENTLY_UNAVAILABLE_LABEL}
                </Typography>
              ) : undefined
            }
            onThumbnailClick={
              isDisabled ? undefined : () => onViewDetails?.(skuOption, () => handleSelectItem(skuOption.id))
            }
            thumbnailAriaLabel={`View details: ${skuOption.name}`}
            variant={HorizontalCardVariantEnum.Suboption}
            sx={{
              border: 'none',
              ...(isDisabled && { opacity: UNAVAILABLE_CARD_OPACITY }),
              boxShadow: ({ palette }) =>
                isItemPicked ? `inset 0 0 0 2px ${palette.primary.main}` : `inset 0 0 0 1px ${palette.divider}`,
            }}
            isDisabled={isDisabled}
            hideDisabledLabel={isUnavailable}
          >
            {!isDisabled && (
              <Button
                variant="text"
                size="small"
                color="primary"
                onClick={(e) => handleViewDetails(e, skuOption, () => handleSelectItem(skuOption.id))}
                sx={optionDetailsButtonSx}
              >
                View details
              </Button>
            )}
            <Radio
              checked={selected?.uid === skuOption.id}
              onChange={() => !isDisabled && handleSelectItem(skuOption.id)}
              value={skuOption.id}
              name={skuOption.id}
              disabled={isDisabled}
              inputProps={{ 'aria-label': skuOption.name }}
              color="primary"
              icon={<RadioButtonUncheckedIcon />}
              checkedIcon={<RadioButtonCheckedIcon />}
            />
          </HorizontalCard>
        </Box>
      );
    });
  }, [sku_options, selected, handleSelectItem, handleViewDetails, disabledItems]);

  return (
    <Accordion elevation={0} expanded={expanded} onChange={handleToggleItem} sx={accordionSx}>
      <AccordionSummary component="div" sx={accordionSummarySx}>
        {renderItem}
      </AccordionSummary>
      <AccordionDetails sx={accordionDetailsSx}>
        <Box sx={optionsHeaderSx}>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            Choose one
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {skuOptionsCount} {getPluralForm(skuOptionsCount, 'option', 'options')}
          </Typography>
        </Box>
        {renderSkuOptions}
      </AccordionDetails>
    </Accordion>
  );
};
